import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isAndroidMobile,
  isIosMobile,
  isMobileWeb,
  isPifAppInstalled,
  isPifAppInstalledSync,
  openContinueApp,
  watchForPifAppInstall,
  subscribePifInstall,
  parseListingDeepLinkFromPath,
} from '../utils/seo';
import './SmartAppInstall.css';

const DISMISS_KEY = 'pif_smart_app_bar_dismissed_v2';

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
};

const isPropertyPath = (pathname = '') =>
  /^\/(?:view\/)?(?:property|project)\/\d+/i.test(pathname)
  || /^\/api\/share\/(?:listing|property)\/\d+/i.test(pathname);

/**
 * Bottom bar for non-property mobile pages.
 * Property pages use PropertyAppPrompt (modal + download bar) instead.
 * Shows Continue App when installed, Get App when not.
 */
const SmartAppInstall = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(() => isPifAppInstalledSync());
  const listing = parseListingDeepLinkFromPath(location.pathname);

  useEffect(() => {
    let cancelled = false;
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;

    const applyKnown = (known) => {
      if (cancelled) return;
      setInstalled(!!known || isPifAppInstalledSync());
    };

    const init = async () => {
      try {
        if (isStandalone()) return;
        if (isPropertyPath(location.pathname)) return;
        if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
        if (!isMobileWeb() && !isAndroidMobile() && !isIosMobile()) return;

        if (!cancelled) setVisible(true);

        const known = await isPifAppInstalled();
        applyKnown(known);

        if (!known && isAndroidMobile()) {
          watchForPifAppInstall({ timeoutMs: 25000, intervalMs: 2000, signal: ac?.signal })
            .then((found) => {
              if (found) applyKnown(true);
            })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) setInstalled(false);
      }
    };

    init();

    const unsubscribe = subscribePifInstall((known) => {
      if (!cancelled) setInstalled(!!known || isPifAppInstalledSync());
    }, () => parseListingDeepLinkFromPath(location.pathname));

    return () => {
      cancelled = true;
      try { ac?.abort(); } catch { /* ignore */ }
      unsubscribe();
    };
  }, [location.pathname]);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const onOpenApp = (e) => {
    e.preventDefault();
    openContinueApp({
      knownInstalled: true,
      propertyId: listing?.propertyId,
      slug: listing?.slug,
      isProject: !!listing?.isProject,
      storeFallback: false,
    });
  };

  const onGetApp = (e) => {
    e.preventDefault();
    openContinueApp({
      knownInstalled: false,
      propertyId: listing?.propertyId,
      slug: listing?.slug,
      isProject: !!listing?.isProject,
      storeFallback: true,
    });
  };

  if (!visible) return null;

  return (
    <div
      className="smart-app-bar"
      role="region"
      aria-label={installed ? 'Continue in the PIF app' : 'Get the PIF app'}
    >
      <div className="smart-app-inner">
        <div className="smart-app-copy">
          <p className="smart-app-title">
            {installed ? 'Continue App' : 'Get the PIF App'}
          </p>
          <p className="smart-app-desc">
            {installed
              ? 'Open the app for the best experience.'
              : 'Download the app for a better experience on your phone.'}
          </p>
        </div>
        <div className="smart-app-actions">
          {installed ? (
            <button type="button" className="smart-app-btn" onClick={onOpenApp}>
              Continue App
            </button>
          ) : (
            <button type="button" className="smart-app-btn" onClick={onGetApp}>
              Get App
            </button>
          )}
          <button type="button" className="smart-app-dismiss" onClick={dismiss}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmartAppInstall;
